import { Link } from "@tanstack/react-router";
import { Mail, MapPin, Phone } from "lucide-react";

import qlsLogo from "@/assets/qls-member-logo.png";
import pexaLogo from "@/assets/pexa-member-logo.png";

import {
  CONTACT_NUMBERS,
  FIRM,
  FIRM_ADDRESS,
  FIRM_POSTAL_ADDRESS,
  PENINSULA_ADDRESS,
  SERVICE_AREAS,
} from "@/lib/firm";

const SERVICE_GROUPS: ReadonlyArray<{
  heading: string;
  links: ReadonlyArray<{ to: string; label: string }>;
}> = [
  {
    heading: "Property",
    links: [
      { to: "/conveyancing", label: "Conveyancing" },
      { to: "/off-the-plan-contracts", label: "Off the plan contracts" },
      { to: "/seller-disclosure", label: "Form 2 seller disclosure" },
      { to: "/commercial-conveyancing", label: "Commercial conveyancing" },
      { to: "/fees", label: "Fixed fees" },
    ],
  },
  {
    heading: "Wills & estates",
    links: [
      { to: "/wills-and-estates", label: "Wills & estates" },
      { to: "/wills-and-probate-queensland", label: "About our wills & probate practice" },
      { to: "/probate", label: "Probate" },
      { to: "/remote-probate", label: "Remote probate (all QLD)" },
      { to: "/transfer-my-will-from-another-firm", label: "Move your will to us" },
      { to: "/testamentary-trusts", label: "Testamentary trusts" },
      { to: "/estate-disputes", label: "Estate disputes" },
      { to: "/elder-law", label: "Elder law" },
      { to: "/granny-flat-consultation", label: "Granny flat consultation" },
    ],
  },
  {
    heading: "Business",
    links: [
      { to: "/business-lawyers", label: "Business & commercial" },
      { to: "/business-sales", label: "Buying & selling a business" },
      { to: "/business-succession-planning", label: "Business succession planning" },
      { to: "/shareholder-agreements", label: "Shareholder agreements" },
      { to: "/selling-a-business-queensland", label: "Selling a business in QLD" },
      { to: "/retail-shop-leases", label: "Retail shop leases & Form 12" },
      { to: "/independent-legal-advice", label: "Independent legal advice" },
      { to: "/aml-real-estate-agents", label: "AML services for agents" },
    ],
  },
  {
    heading: "Resources",
    links: [
      { to: "/faqs", label: "FAQs" },
      { to: "/guides", label: "Legal guides" },
      { to: "/local-guides", label: "Golden Beach & Caloundra reading" },
      { to: "/blog", label: "Blog" },
      { to: "/agent-updates", label: "Agent & broker update" },
      { to: "/mortgage-broker-conveyancing-qld", label: "For mortgage brokers" },
      { to: "/our-people", label: "Our people" },
      { to: "/contact", label: "Contact" },
      { to: "/privacy", label: "Privacy" },
    ],
  },
];

const AREA_LINKS: ReadonlyArray<{ to: string; label: string }> = [
  { to: "/golden-beach", label: "Golden Beach" },
  { to: "/caloundra", label: "Caloundra" },
  { to: "/pelican-waters", label: "Pelican Waters" },
  { to: "/kings-beach", label: "Kings Beach" },
  { to: "/moffat-beach", label: "Moffat Beach" },
  { to: "/currimundi", label: "Currimundi" },
  { to: "/little-mountain", label: "Little Mountain" },
  { to: "/baringa", label: "Baringa & Aura" },
  { to: "/shelly-beach", label: "Shelly Beach" },
  { to: "/dicky-beach", label: "Dicky Beach" },
  { to: "/battery-hill", label: "Battery Hill" },
  { to: "/aroona", label: "Aroona" },
  { to: "/wurtulla", label: "Wurtulla" },
  { to: "/meridan-plains", label: "Meridan Plains" },
  { to: "/mooloolaba", label: "Mooloolaba" },
  { to: "/maroochydore", label: "Maroochydore" },
  { to: "/buderim", label: "Buderim" },
  { to: "/noosa", label: "Noosa" },
  { to: "/conveyancing-sunshine-coast", label: "Sunshine Coast conveyancing" },
  { to: "/conveyancing/caloundra", label: "Caloundra conveyancing" },
  { to: "/conveyancing-brisbane", label: "Brisbane conveyancing" },
  { to: "/moreton-bay", label: "Moreton Bay region" },
  { to: "/remote-probate", label: "Probate anywhere in QLD" },
];


export function SiteFooter() {
  return (
    <footer className="mt-24 border-t border-border bg-secondary/60">
      <div className="mx-auto max-w-6xl px-5 py-14">
        <div className="grid gap-10 lg:grid-cols-[1.1fr_2fr]">
          <div>
            <p className="brand-wordmark text-base">{FIRM.name}</p>
            <p className="mt-3 max-w-sm text-sm leading-relaxed text-muted-foreground">
              A Golden Beach law firm serving Caloundra and the Sunshine Coast.
            </p>

            <ul className="mt-6 space-y-3 text-sm text-muted-foreground">
              <li className="flex gap-2">
                <MapPin className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                <span>{FIRM_ADDRESS}</span>
              </li>
              <li className="flex gap-2">
                <MapPin className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                <span>{PENINSULA_ADDRESS} (by appointment)</span>
              </li>
              <li className="flex gap-2">
                <Mail className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                <span>Postal: {FIRM_POSTAL_ADDRESS}</span>
              </li>
              {CONTACT_NUMBERS.map((c) => (
                <li key={c.phone} className="flex gap-2">
                  <Phone className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                  <a href={c.phoneHref} className="hover:text-foreground">
                    {c.phone} <span className="text-xs">({c.name.split(" ")[0]}, {c.office})</span>
                  </a>
                </li>
              ))}
              <li className="flex gap-2">
                <Mail className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                <a href={FIRM.emailHref} className="hover:text-foreground">
                  {FIRM.email}
                </a>
              </li>
              <li className="pt-1">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center rounded-md bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90"
                >
                  Request a consultation
                </Link>
              </li>
              <li>
                <Link to="/reviews" className="font-semibold text-primary hover:underline">
                  Client reviews
                </Link>
              </li>
              <li className="flex flex-wrap gap-x-4 gap-y-2">
                <a href="https://maps.google.com/maps?cid=17645464718929886346" target="_blank" rel="noopener noreferrer" className="hover:text-foreground">
                  Find us on Google
                </a>
                <a href="https://search.google.com/local/writereview?placeid=ChIJ4VpKvNSZk2sRivyAFKxI4fQ" target="_blank" rel="noopener noreferrer" className="hover:text-foreground">
                  Leave a Google review
                </a>
              </li>
              <li className="flex flex-wrap gap-x-4 gap-y-2 pt-1">
                <a href="https://www.facebook.com/profile.php?id=61586552011485" target="_blank" rel="noopener noreferrer" className="hover:text-foreground">
                  Facebook
                </a>
                <a href="https://www.instagram.com/coastsidelaw/" target="_blank" rel="noopener noreferrer" className="hover:text-foreground">
                  Instagram
                </a>
                <a href="https://www.linkedin.com/company/143455959/" target="_blank" rel="noopener noreferrer" className="hover:text-foreground">
                  LinkedIn
                </a>
              </li>
            </ul>
          </div>

          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {SERVICE_GROUPS.map((group) => (
              <div key={group.heading}>
                <p className="text-xs font-semibold uppercase tracking-[0.15em] text-foreground">
                  {group.heading}
                </p>
                <ul className="mt-4 space-y-1 text-sm">
                  {group.links.map((link) => (
                    <li key={link.to}>
                      <Link to={link.to} className="inline-block py-1.5 text-muted-foreground hover:text-foreground">
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <details className="group mt-12 border-t border-border/70 pt-6">
          <summary className="flex cursor-pointer list-none items-center justify-between text-xs font-semibold uppercase tracking-[0.15em] text-foreground">
            Areas we serve
            <span className="text-muted-foreground transition-transform group-open:rotate-180" aria-hidden="true">▾</span>
          </summary>
          <ul className="mt-4 flex flex-wrap gap-x-2 gap-y-2 text-sm">
            {AREA_LINKS.map((area) => (
              <li key={area.to}>
                <Link
                  to={area.to}
                  className="inline-block rounded-full border border-border/70 bg-background/60 px-3 py-1 text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground"
                >
                  {area.label}
                </Link>
              </li>
            ))}
          </ul>
        </details>
      </div>

      <div className="border-t border-border/70">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-x-8 gap-y-4 px-5 py-6">
          <img
            src={qlsLogo}
            alt="Member of the Queensland Law Society"
            loading="lazy"
            width={281}
            height={100}
            className="h-10 w-auto shrink-0"
          />
          <img
            src={pexaLogo}
            alt="PEXA member — electronic conveyancing"
            loading="lazy"
            width={195}
            height={100}
            className="h-10 w-auto shrink-0"
          />
          <p className="text-sm text-muted-foreground">{FIRM.membership}</p>
        </div>
      </div>

      <div className="border-t border-border/70">
        <div className="mx-auto max-w-6xl px-5 py-6 text-xs leading-relaxed text-foreground/80">
          <p>
            Serving {SERVICE_AREAS.slice(0, -1).join(", ")} and {SERVICE_AREAS.at(-1)}.
          </p>
          <p className="mt-3">
            © {new Date().getFullYear()} {FIRM.name}. Liability limited by a scheme approved under
            professional standards legislation. The information on this website is general in nature
            and is not legal advice for your situation — please contact us for advice about your
            matter.
          </p>
        </div>
      </div>
    </footer>
  );
}
